// 敵GLBのアニメーション統合（2026-08-08 指示）: Meshy が生成する「戦闘用」「移動用」の
// 2つのアニメ付きGLBを、本家 sevenfold/scripts/merge-enemy-glb.mjs と同じ方式で
// 1ファイル（idle / walk の2クリップ）へ統合する。
//
// エンジン（js/entity/entity-spawning.ts・enemy-ai.ts）はクリップ名を
// name.includes('idle'/'walk') で検索するため、クリップ名の正規化が本体。
// walk 側のリグがベースと不一致（対象ノード名が存在しない）の場合は、idle クリップの
// 先頭フレーム姿勢を全チャンネルで固定した「静止 walk クリップ」で代替する
// （空クリップだと weight 切替時に未指定関節がバインドポーズへ落ちるため）。
//
// Node（CLI/サーバ）とブラウザ完結版の両方から使うため、fs に依存しない
// WebIO + readBinary/writeBinary のみで実装する。
import { Document, WebIO, VertexLayout } from '@gltf-transform/core';
import { ALL_EXTENSIONS } from '@gltf-transform/extensions';

/** 静止 walk クリップの再生時間 (秒)。全キー同一値のため長さに視覚的意味はない */
const STATIC_WALK_DURATION_SEC = 1;

/**
 * 頂点属性は必ず SEPARATE で書き出す。gltf-transform 既定の INTERLEAVED だと
 * three.js GLTFLoader が共有 InterleavedBuffer を生成し、WebGPURenderer で
 * 敵死亡時の geometry.dispose() 後に同型モデルの生存個体が破棄済みバッファを
 * 参照し続け「Buffer used in submit while destroyed」を毎フレーム出す
 * （本家 2026-08-08 実機再現で特定。Blender 出力の SEPARATE 配置では発生しない）。
 */
function createIo() {
    return new WebIO().registerExtensions(ALL_EXTENSIONS).setVertexLayout(VertexLayout.SEPARATE);
}

/** アニメーションの再生時間 (input アクセッサの最大値) を求める */
function animationDuration(anim) {
    let dur = 0;
    for (const sampler of anim.listSamplers()) {
        const input = sampler.getInput();
        if (!input) continue;
        const max = input.getMax([]);
        if (Number.isFinite(max?.[0])) dur = Math.max(dur, max[0]);
    }
    return dur;
}

/**
 * move ドキュメントの先頭アニメーションを base ドキュメントへノード名でリターゲット追記する。
 * @returns {{ ok: true, channels: number } | { ok: false, reason: string }}
 */
function appendWalkAnimation(baseDoc, moveDoc) {
    const baseRoot = baseDoc.getRoot();
    const moveAnims = moveDoc.getRoot().listAnimations();
    if (moveAnims.length === 0) return { ok: false, reason: 'move側にアニメーションがない' };
    const srcAnim = moveAnims[0];

    // ノード名 → ベースNode の索引 (重名はリターゲット先が曖昧になるため中断)
    const baseNodes = new Map();
    for (const node of baseRoot.listNodes()) {
        const name = node.getName();
        if (baseNodes.has(name)) return { ok: false, reason: `ベース側にノード重名: "${name}"` };
        baseNodes.set(name, node);
    }

    // 事前互換チェック: 全チャンネルの対象ノード名がベースに存在すること
    for (const channel of srcAnim.listChannels()) {
        const targetNode = channel.getTargetNode();
        if (!targetNode) return { ok: false, reason: '対象ノードのないチャンネルがある' };
        if (channel.getTargetPath() === 'weights') return { ok: false, reason: 'モーフターゲットは未対応' };
        if (!baseNodes.has(targetNode.getName())) {
            return { ok: false, reason: `リグ不一致 (ベースに "${targetNode.getName()}" が存在しない)` };
        }
    }

    const buffer = baseRoot.listBuffers()[0] ?? baseDoc.createBuffer();
    const accessorCopies = new Map();
    const copyAccessor = (src) => {
        let dst = accessorCopies.get(src);
        if (!dst) {
            dst = baseDoc.createAccessor(src.getName())
                .setType(src.getType())
                .setArray(src.getArray())
                .setNormalized(src.getNormalized())
                .setBuffer(buffer);
            accessorCopies.set(src, dst);
        }
        return dst;
    };

    const dstAnim = baseDoc.createAnimation('walk');
    const samplerCopies = new Map();
    for (const channel of srcAnim.listChannels()) {
        const srcSampler = channel.getSampler();
        if (!srcSampler) continue;
        let dstSampler = samplerCopies.get(srcSampler);
        if (!dstSampler) {
            dstSampler = baseDoc.createAnimationSampler()
                .setInput(copyAccessor(srcSampler.getInput()))
                .setOutput(copyAccessor(srcSampler.getOutput()))
                .setInterpolation(srcSampler.getInterpolation());
            samplerCopies.set(srcSampler, dstSampler);
            dstAnim.addSampler(dstSampler);
        }
        const dstChannel = baseDoc.createAnimationChannel()
            .setTargetNode(baseNodes.get(channel.getTargetNode().getName()))
            .setTargetPath(channel.getTargetPath())
            .setSampler(dstSampler);
        dstAnim.addChannel(dstChannel);
    }
    return { ok: true, channels: dstAnim.listChannels().length };
}

/**
 * idle クリップの先頭フレーム姿勢を全チャンネルで固定した静止 walk クリップを生成する。
 * @returns {{ ok: true, channels: number } | { ok: false, reason: string }}
 */
function appendStaticWalkAnimation(baseDoc, idleAnim) {
    const buffer = baseDoc.getRoot().listBuffers()[0] ?? baseDoc.createBuffer();
    const timesAccessor = baseDoc.createAccessor()
        .setType('SCALAR')
        .setArray(new Float32Array([0, STATIC_WALK_DURATION_SEC]))
        .setBuffer(buffer);
    const dstAnim = baseDoc.createAnimation('walk');
    let channels = 0;
    for (const channel of idleAnim.listChannels()) {
        const srcSampler = channel.getSampler();
        const output = srcSampler?.getOutput();
        const targetNode = channel.getTargetNode();
        if (!srcSampler || !output || !targetNode) continue;
        if (channel.getTargetPath() === 'weights') {
            return { ok: false, reason: 'モーフターゲットは未対応' };
        }
        const elementSize = output.getElementSize();
        const src = output.getArray();
        // CUBICSPLINE のキーは [inTangent, 値, outTangent] 並びのため値本体は中央要素
        const firstOffset = srcSampler.getInterpolation() === 'CUBICSPLINE' ? elementSize : 0;
        const first = src.slice(firstOffset, firstOffset + elementSize);
        const held = new src.constructor(elementSize * 2);
        held.set(first, 0);
        held.set(first, elementSize);
        const dstSampler = baseDoc.createAnimationSampler()
            .setInput(timesAccessor)
            .setOutput(
                baseDoc.createAccessor()
                    .setType(output.getType())
                    .setArray(held)
                    .setNormalized(output.getNormalized())
                    .setBuffer(buffer)
            )
            .setInterpolation('STEP');
        dstAnim.addSampler(dstSampler);
        dstAnim.addChannel(
            baseDoc.createAnimationChannel()
                .setTargetNode(targetNode)
                .setTargetPath(channel.getTargetPath())
                .setSampler(dstSampler)
        );
        channels++;
    }
    if (channels === 0) return { ok: false, reason: 'idle側に複製可能なチャンネルがない' };
    return { ok: true, channels };
}

/**
 * 戦闘用（idleベース）と移動用のアニメ付きGLBバイナリを 1 ファイルへ統合する。
 * リグ不一致で walk を持ち込めない場合は静止 walk クリップへ自動フォールバックする
 * （Meshy は同一 rig_task_id から両アニメを作るため通常は発生しない）。
 *
 * @param {Uint8Array} idleBytes 戦闘用（idle として採用する）GLB
 * @param {Uint8Array} walkBytes 移動用（walk として追記する）GLB
 * @param {{ onLog?: (line: string) => void }} [opts]
 * @returns {Promise<{ bytes: Uint8Array, walkMode: 'retarget'|'static', channels: number }>}
 */
export async function mergeEnemyAnimationGlb(idleBytes, walkBytes, { onLog = () => {} } = {}) {
    const io = createIo();
    const baseDoc = await io.readBinary(new Uint8Array(idleBytes));
    const baseAnims = baseDoc.getRoot().listAnimations();
    if (baseAnims.length === 0) {
        throw new Error('戦闘用GLBにアニメーションがありません（idle クリップを作れません）');
    }
    // Meshy のアニメ生成は 1 タスク 1 クリップ。想定外に複数あっても先頭を idle として採用する
    baseAnims[0].setName('idle');
    for (const extra of baseAnims.slice(1)) {
        onLog(`    ⚠ 戦闘用GLBの余剰アニメーション「${extra.getName()}」を除去します`);
        extra.dispose();
    }

    let walkMode = 'retarget';
    const moveDoc = await io.readBinary(new Uint8Array(walkBytes));
    let appended = appendWalkAnimation(baseDoc, moveDoc);
    if (!appended.ok) {
        onLog(`    ⚠ walk のリターゲット統合に失敗したため静止 walk で代替します（${appended.reason}）`);
        walkMode = 'static';
        appended = appendStaticWalkAnimation(baseDoc, baseDoc.getRoot().listAnimations()[0]);
    }
    if (!appended.ok) {
        throw new Error(`walk クリップを統合できません: ${appended.reason}`);
    }

    const bytes = await io.writeBinary(baseDoc);

    // 出力を読み直してクリップ構成を検証する（本家 merge-enemy-glb.mjs と同じ最終確認）
    const verifyDoc = await io.readBinary(bytes);
    const names = verifyDoc.getRoot().listAnimations().map((a) => a.getName());
    if (!names.includes('idle') || !names.includes('walk')) {
        throw new Error(`統合GLBのクリップ検証に失敗しました（実際: ${names.join(',') || 'なし'}）`);
    }
    return { bytes, walkMode, channels: appended.channels };
}

/** テスト用に内部関数と Document を公開する（実運用では mergeEnemyAnimationGlb のみを使う） */
export const __internal = { appendWalkAnimation, appendStaticWalkAnimation, animationDuration, createIo, Document };
